import { useRef, useState } from "react";
import { UploadCloud, X, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export function PhotoUploader({ value = [], onChange, min = 5 }) {
  const inputRef = useRef(null);
  const [items, setItems] = useState(value);
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(0);

  const upload = async (files) => {
    const arr = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (!arr.length) return;
    setUploading(arr.length);
    let current = items;
    for (const f of arr) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        current = [...current, r.data.url];
        setItems(current);
        onChange?.(current);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
      }
      setUploading((u) => Math.max(0, u - 1));
    }
  };

  const remove = (url) => {
    const next = items.filter((u) => u !== url);
    setItems(next);
    onChange?.(next);
  };

  const enough = items.length >= min;

  return (
    <div data-testid="photo-uploader">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-6 text-center transition-colors ${drag ? "border-blue-500 bg-blue-50" : "border-slate-300 hover:border-slate-400 bg-slate-50"}`}
      >
        <UploadCloud className="mx-auto text-blue-600" size={28} />
        <p className="text-sm font-medium text-slate-700 mt-2">Drag &amp; drop photos, or click to browse</p>
        <p className="text-xs text-slate-400 mt-1">JPEG / PNG / WebP · at least {min} photos · up to 12 MB each</p>
        <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" data-testid="photo-input"
          onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
      </div>

      <div className="flex items-center justify-between mt-3">
        <span className={`text-xs font-semibold ${enough ? "text-green-600" : "text-amber-600"}`} data-testid="photo-count">
          {items.length} / {min} photos {enough ? "✓" : "required"}
        </span>
        {uploading > 0 && <span className="text-xs text-slate-500 inline-flex items-center gap-1"><Loader2 size={13} className="animate-spin" /> Uploading {uploading}…</span>}
      </div>

      {items.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-3">
          {items.map((url, i) => (
            <div key={url} data-testid={`photo-thumb-${i}`} className="relative aspect-square rounded-xl overflow-hidden border border-slate-200 group">
              <img src={url} alt="" className="w-full h-full object-cover" />
              {i === 0 && <span className="absolute bottom-1 left-1 bg-blue-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">COVER</span>}
              <button type="button" onClick={(e) => { e.stopPropagation(); remove(url); }} data-testid={`photo-remove-${i}`}
                className="absolute top-1 right-1 w-6 h-6 grid place-items-center rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                <X size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
